import { ApiError, apiErrorCode } from "@/lib/api";

// One place to turn an unknown caught value into a user-facing message
// (CLAUDE.md #7 — failures surface as handled UI states, never a white-screen).
//
// BRK-S279 — NO SERVER TEXT FOR A REFUSAL THE PORTAL CANNOT NAME. A 4xx without
// a `details.code` used to reach the broker verbatim, which is how a broker
// read "signers: Invalid input" on the GIA section (PMS design
// account-requirements.md, SB-01619 step 6). The portal now speaks for itself:
//   • a 4xx with no code → a fixed sentence per status, never `error`;
//   • `VALIDATION_FAILED` → the portal's own sentence (per field where a form
//     can anchor it — see `validationFailures`), never `error` or a `reason`;
//   • any other code → unchanged: a coded refusal's `error` is a sentence PMS
//     wrote for the broker, and screens that know the code already say more.
// 5xx and transport failures are unchanged too: PMS answers those generically
// by design (no internal detail leaks), and the client-built ones are ours.

/** The floor for anything that is not an `ApiError`. */
export const SOMETHING_WENT_WRONG = "Something went wrong. Please try again.";

/** A refused request the portal has no specific sentence for. */
export const REQUEST_REFUSED =
  "We couldn’t complete that. Please check what you entered and try again.";

/** `VALIDATION_FAILED` that no field on the screen can carry. */
export const VALIDATION_SUMMARY =
  "Some of the information wasn’t valid. Please check it and try again.";

/** The fixed sentence for a code-less 4xx, by status. */
export const REFUSAL_BY_STATUS: Readonly<Record<number, string>> = {
  401: "Your session has ended. Please sign in again.",
  403: "You don’t have access to this.",
  404: "That isn’t available. It may not be ready yet, or it may have been removed.",
  409: "This changed since you opened it. Reload the page and try again.",
};

/**
 * `details.fields[].reason` on a `VALIDATION_FAILED` refusal — PMS's closed set
 * (PMS-S609, `BrokerValidationFailed` in PMS's docs/openapi.yaml). Pinned since
 * BRK-S280: `brokerBondsContractPin.test.ts` holds this union equal to the
 * published enum.
 */
export type ValidationReason =
  | "required"
  | "too_short"
  | "too_long"
  | "invalid";

/** The portal's words for each reason. The server's are never shown. */
export const VALIDATION_REASON_COPY: Readonly<
  Record<ValidationReason, string>
> = {
  required: "This is required.",
  too_short: "This is too short.",
  too_long: "This is too long.",
  invalid: "This isn’t valid.",
};

/** A reason off the wire, read into the closed set. */
export function readValidationReason(reason: unknown): ValidationReason {
  // An UNKNOWN reason is `invalid` — never a throw, never its own text:
  // discovery's own VALIDATION_FAILED still passes through on
  // /api/broker/bond/** with free-text reasons until PMS-S627.
  return reason === "required" ||
    reason === "too_short" ||
    reason === "too_long"
    ? reason
    : "invalid";
}

/** One field a `VALIDATION_FAILED` refusal names, its reason already read. */
export interface ValidationFailure {
  /** The request path as the server spelled it (`signers.0.email`,
   *  `personal[0].ssn`); `""` is the body itself. */
  path: string;
  reason: ValidationReason;
}

/**
 * The fields of a `VALIDATION_FAILED` refusal, or null when `err` is not one.
 *
 * Malformed entries are dropped rather than thrown on (NN #7); a refusal with
 * no usable fields is still a validation failure — an empty array.
 */
export function validationFailures(err: unknown): ValidationFailure[] | null {
  if (!(err instanceof ApiError) || apiErrorCode(err) !== "VALIDATION_FAILED") {
    return null;
  }
  // `apiErrorCode` has already established `body.details` is an object.
  const details = (err.body as { details: { fields?: unknown } }).details;
  const fields = Array.isArray(details.fields) ? details.fields : [];
  const out: ValidationFailure[] = [];
  for (const entry of fields) {
    if (typeof entry !== "object" || entry === null) continue;
    const { path, reason } = entry as { path?: unknown; reason?: unknown };
    if (typeof path !== "string") continue;
    out.push({ path, reason: readValidationReason(reason) });
  }
  return out;
}

/** The portal's sentence for one validation reason. */
export function validationReasonCopy(reason: ValidationReason): string {
  return VALIDATION_REASON_COPY[reason];
}

/**
 * The portal's sentence for a 4xx it has no specific copy for, or null when
 * `err` is not a 4xx or carries a code other than `VALIDATION_FAILED` — its own
 * message then still stands.
 */
export function uncodedRefusalMessage(err: unknown): string | null {
  if (!(err instanceof ApiError) || err.status < 400 || err.status >= 500) {
    return null;
  }
  const code = apiErrorCode(err);
  if (code === "VALIDATION_FAILED") return VALIDATION_SUMMARY;
  if (code !== null) return null;
  return REFUSAL_BY_STATUS[err.status] ?? REQUEST_REFUSED;
}

export function messageFor(err: unknown): string {
  if (!(err instanceof ApiError)) return SOMETHING_WENT_WRONG;
  return uncodedRefusalMessage(err) ?? err.message;
}
